/* eslint-disable @typescript-eslint/no-use-before-define -- the editor is
   declared first for readability; the sub-components it composes are
   `function` declarations and therefore hoisted at runtime (same pattern as
   AiActionDialog). */
import {
  ArrowDownIcon,
  ArrowUpIcon,
  TrashIcon,
} from "@heroicons/react/24/outline"
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react"
import type {
  BrandKit,
  OverlayDoc,
  OverlayItem,
  OverlaySection,
  OverlayTextItem,
} from "~/ui-rtk/api/adForgeApi"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { ensureFontsLoaded, fontStack, solveDoc } from "./render-overlay"
import { fontFamiliesInDoc } from "./overlay-solver"

/**
 * Structural editor for the flow overlay document ("divs with flexbox"):
 * sections anchored on the canvas, items stacked inside. The view renders
 * the SAME solved boxes the exporter draws, so editing is exactly WYSIWYG.
 * Editing is structural — select a section or item, tweak it in the
 * inspector, reorder with ↑/↓ — which is what makes overlap impossible.
 */

let idSeq = 0
const newId = (prefix: string): string => {
  idSeq += 1
  return `${prefix}-${Date.now().toString(36)}-${idSeq}`
}

// ── Item / section factories (brand-kit aware defaults) ──────────────────

export function makeSection(
  doc: OverlayDoc,
  anchor: OverlaySection["anchor"],
): OverlaySection {
  const { width, height } = doc.canvas
  return {
    id: newId(`section-${anchor}`),
    anchor,
    direction: "column",
    align: "start",
    justify: "start",
    gap: Math.round(height * 0.015),
    paddingX: Math.round(width * 0.08),
    paddingY: Math.round(height * 0.08),
    items: [],
  }
}

export function makeTextItem(
  doc: OverlayDoc,
  brand: BrandKit | undefined,
  kind: "tag" | "headline" | "subline" | "button",
): OverlayTextItem {
  const { height } = doc.canvas
  const heading = brand?.fonts?.heading || "Inter"
  const body = brand?.fonts?.body || heading
  if (kind === "tag") {
    return {
      id: newId("tag"),
      type: "text",
      text: "NEW TAG",
      fontFamily: body,
      fontWeight: 700,
      fontSize: Math.round(height * 0.022),
      // Brand kit first: tag text sits on the accent pill, so the kit's
      // background color gives the intended contrast pair.
      color: brand?.colors?.background || "#FFFFFF",
      lineHeight: 1,
      uppercase: true,
      letterSpacing: Math.round(height * 0.004),
      background: {
        color: brand?.colors?.accent || brand?.colors?.primary || "#C43B3B",
        paddingX: Math.round(height * 0.012),
        paddingY: Math.round(height * 0.008),
        radius: 0,
      },
    }
  }
  if (kind === "button") {
    return {
      id: newId("button"),
      type: "text",
      text: "Shop now",
      fontFamily: body,
      fontWeight: 700,
      fontSize: Math.round(height * 0.028),
      color: brand?.colors?.background || "#FFFFFF",
      lineHeight: 1,
      background: {
        color: brand?.colors?.primary || "#111111",
        paddingX: Math.round(height * 0.024),
        paddingY: Math.round(height * 0.012),
        radius: 999,
      },
    }
  }
  if (kind === "subline") {
    return {
      id: newId("subline"),
      type: "text",
      text: "A short supporting line",
      fontFamily: body,
      fontWeight: 500,
      fontSize: Math.round(height * 0.026),
      color: brand?.colors?.text || "#FFFFFF",
      lineHeight: 1.25,
      shadow: true,
    }
  }
  return {
    id: newId("headline"),
    type: "text",
    text: "YOUR HEADLINE",
    fontFamily: brand?.fonts?.heading || "Inter",
    fontWeight: 900,
    fontSize: Math.round(height * 0.07),
    color: brand?.colors?.text || "#FFFFFF",
    lineHeight: 1.05,
    uppercase: true,
    background: {
      color: brand?.colors?.secondary || "#000000",
      paddingX: Math.round(height * 0.018),
      paddingY: Math.round(height * 0.01),
      radius: 0,
    },
  }
}

export function makeDividerItem(
  doc: OverlayDoc,
  brand: BrandKit | undefined,
): OverlayItem {
  return {
    id: newId("bar"),
    type: "divider",
    width: Math.round(doc.canvas.width * 0.12),
    height: 8,
    color: brand?.colors?.primary || "#FFFFFF",
    radius: 4,
  }
}

export function makeLogoItem(doc: OverlayDoc): OverlayItem {
  const h = Math.round(doc.canvas.height * 0.045)
  return { id: newId("logo"), type: "logo", height: h, width: h * 3 }
}

export function makeSpacerItem(doc: OverlayDoc): OverlayItem {
  return {
    id: newId("spacer"),
    type: "spacer",
    size: Math.round(doc.canvas.height * 0.03),
  }
}

/** #RRGGBB + opacity → #RRGGBBAA so DOM fills match the canvas renderer. */
const hexWithAlpha = (color: string, opacity: number): string =>
  color.length === 9
    ? color
    : `${color}${Math.round(Math.min(1, Math.max(0, opacity)) * 255)
        .toString(16)
        .padStart(2, "0")}`

const GRADIENT_CSS_ANGLE: Record<string, string> = {
  vertical: "180deg",
  horizontal: "90deg",
  diagonal: "135deg",
}

// ── Editor ───────────────────────────────────────────────────────────────

// Subtle checkerboard so builder mode reads as "no media yet".
const CHECKERBOARD: CSSProperties = {
  backgroundColor: "#1f2430",
  backgroundImage:
    "linear-gradient(45deg, #2a3040 25%, transparent 25%, transparent 75%, #2a3040 75%), linear-gradient(45deg, #2a3040 25%, transparent 25%, transparent 75%, #2a3040 75%)",
  backgroundSize: "24px 24px",
  backgroundPosition: "0 0, 12px 12px",
}

interface OverlayCanvasEditorProps {
  doc: OverlayDoc
  onChange: (doc: OverlayDoc) => void
  /** Null → layout-builder mode: a neutral checkerboard stands in for media. */
  media: { type: "image" | "video"; src: string } | null
  logoUrl?: string | null
  brandKit?: BrandKit
  /** Selected section OR item id. */
  selectedId: string | null
  onSelectId: (id: string | null) => void
}

type Selection =
  | { kind: "section"; section: OverlaySection }
  | { kind: "item"; section: OverlaySection; item: OverlayItem }
  | null

function findSelection(doc: OverlayDoc, id: string | null): Selection {
  if (!id) return null
  for (const section of doc.sections) {
    if (section.id === id) return { kind: "section", section }
    for (const item of section.items) {
      if (item.id === id) return { kind: "item", section, item }
    }
  }
  return null
}

export default function OverlayCanvasEditor({
  doc,
  onChange,
  media,
  logoUrl,
  brandKit,
  selectedId,
  onSelectId,
}: OverlayCanvasEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [displayWidth, setDisplayWidth] = useState(0)
  // Re-solve after web fonts finish loading — measurements change.
  const [fontsVersion, setFontsVersion] = useState(0)

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const observer = new ResizeObserver(entries => {
      setDisplayWidth(entries[0]?.contentRect.width ?? 0)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const families = fontFamiliesInDoc(doc)
    if (!families.length) return
    let cancelled = false
    void ensureFontsLoaded(families).then(() => {
      if (!cancelled) setFontsVersion(v => v + 1)
    })
    return () => {
      cancelled = true
    }
  }, [doc])

  // fontsVersion forces re-measure after web fonts finish loading.
  const layout = useMemo(() => solveDoc(doc), [doc, fontsVersion])

  const scale = displayWidth > 0 ? displayWidth / doc.canvas.width : 0
  const selection = findSelection(doc, selectedId)

  // ── Structure mutations ────────────────────────────────────────────────

  const patchSection = (id: string, patch: Partial<OverlaySection>): void => {
    onChange({
      ...doc,
      sections: doc.sections.map(section =>
        section.id === id ? { ...section, ...patch } : section,
      ),
    })
  }

  const patchItem = (id: string, patch: Partial<OverlayItem>): void => {
    onChange({
      ...doc,
      sections: doc.sections.map(section => ({
        ...section,
        items: section.items.map(item =>
          item.id === id ? ({ ...item, ...patch } as OverlayItem) : item,
        ),
      })),
    })
  }

  const removeSelected = (): void => {
    if (!selection) return
    if (selection.kind === "section") {
      onChange({
        ...doc,
        sections: doc.sections.filter(s => s.id !== selection.section.id),
      })
    } else {
      onChange({
        ...doc,
        sections: doc.sections.map(section => ({
          ...section,
          items: section.items.filter(item => item.id !== selection.item.id),
        })),
      })
    }
    onSelectId(null)
  }

  const moveItem = (direction: -1 | 1): void => {
    if (selection?.kind !== "item") return
    onChange({
      ...doc,
      sections: doc.sections.map(section => {
        const index = section.items.findIndex(
          item => item.id === selection.item.id,
        )
        if (index < 0) return section
        const target = index + direction
        if (target < 0 || target >= section.items.length) return section
        const items = [...section.items]
        ;[items[index], items[target]] = [items[target], items[index]]
        return { ...section, items }
      }),
    })
  }

  const addSection = (anchor: OverlaySection["anchor"]): void => {
    const section = makeSection(doc, anchor)
    onChange({ ...doc, sections: [...doc.sections, section] })
    onSelectId(section.id)
  }

  const addItem = (item: OverlayItem): void => {
    // Into the selected section (or the selected item's section); create a
    // bottom section if none exists yet.
    let targetId = selection?.section.id
    let nextDoc = doc
    if (!targetId) {
      const section = makeSection(doc, "bottom")
      nextDoc = { ...doc, sections: [...doc.sections, section] }
      targetId = section.id
    }
    onChange({
      ...nextDoc,
      sections: nextDoc.sections.map(section =>
        section.id === targetId
          ? { ...section, items: [...section.items, item] }
          : section,
      ),
    })
    onSelectId(item.id)
  }

  // ── Drag-to-move ───────────────────────────────────────────────────────
  // Dragging an item detaches it from the flow: on drop it gets an absolute
  // `position` (canvas-space) and floats freely. The inspector's "Dock"
  // button clears the position and returns the item to its section's stack.

  const dragRef = useRef<{
    id: string
    startX: number
    startY: number
    originX: number
    originY: number
    moved: boolean
  } | null>(null)
  const [dragState, setDragState] = useState<{
    id: string
    dx: number
    dy: number
  } | null>(null)

  const handleItemPointerDown = (
    e: ReactPointerEvent<HTMLDivElement>,
    itemId: string,
    originX: number,
    originY: number,
  ): void => {
    e.stopPropagation()
    onSelectId(itemId)
    dragRef.current = {
      id: itemId,
      startX: e.clientX,
      startY: e.clientY,
      originX,
      originY,
      moved: false,
    }
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const handleItemPointerMove = (
    e: ReactPointerEvent<HTMLDivElement>,
  ): void => {
    const drag = dragRef.current
    if (!drag) return
    const dx = e.clientX - drag.startX
    const dy = e.clientY - drag.startY
    // Small threshold so plain clicks stay clicks.
    if (!drag.moved && Math.hypot(dx, dy) < 5) return
    drag.moved = true
    setDragState({ id: drag.id, dx, dy })
  }

  const handleItemPointerUp = (e: ReactPointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current
    dragRef.current = null
    setDragState(null)
    if (!drag?.moved || scale === 0) return
    // The item lands exactly where its ghost was: original box origin plus
    // the drag delta, stored as PERCENTAGES of the canvas (ratio-independent)
    // and clamped so it can't be lost off-canvas. Setting `position`
    // detaches it from the flow (absolute placement).
    const x = drag.originX + (e.clientX - drag.startX) / scale
    const y = drag.originY + (e.clientY - drag.startY) / scale
    const pct = (value: number): number => Math.round(value * 100) / 100
    patchItem(drag.id, {
      position: {
        xPct: pct(Math.min(98, Math.max(-25, (x / doc.canvas.width) * 100))),
        yPct: pct(Math.min(98, Math.max(-25, (y / doc.canvas.height) * 100))),
      },
    })
  }

  // ── Render ─────────────────────────────────────────────────────────────

  /**
   * Anchor-faithful vertical CSS: bottom-anchored sections position with
   * `bottom` (the inset reads directly, no derived fractional `top`), while
   * top/center sections and free-placed items keep `top`. Same geometry
   * either way — the solver's y stays the source of truth.
   */
  const verticalStyle = (
    yTop: number,
    height: number,
    anchor?: OverlaySection["anchor"],
    isFreePlaced?: boolean,
  ): CSSProperties =>
    anchor === "bottom" && !isFreePlaced
      ? { bottom: (doc.canvas.height - yTop - height) * scale }
      : { top: yTop * scale }

  const anchorForSection = (sectionId: string): OverlaySection["anchor"] =>
    doc.sections.find(section => section.id === sectionId)?.anchor ?? "top"

  // Selection/hover feedback as BUILT STYLES, not utility classes — these
  // are dynamic editor visuals and inline styles can't be lost to theme or
  // purge behavior (matches themes/dark.js `brand`).
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const BRAND = "#EB89B5"
  const selectionStyle = (id: string): CSSProperties =>
    id === selectedId
      ? { boxShadow: `0 0 0 2px ${BRAND}` }
      : id === hoveredId
        ? { boxShadow: `0 0 0 1px ${BRAND}66` }
        : {}
  const hoverHandlers = (id: string) => ({
    onPointerEnter: () => setHoveredId(id),
    onPointerLeave: () => setHoveredId(prev => (prev === id ? null : prev)),
  })

  const dragStyle = (itemId: string): CSSProperties =>
    dragState?.id === itemId
      ? {
          transform: `translate(${dragState.dx}px, ${dragState.dy}px)`,
          zIndex: 20,
          opacity: 0.85,
        }
      : {}

  return (
    <div className="flex flex-col gap-2">
      {/* Structure toolbar */}
      <div className="flex items-center gap-1.5 flex-wrap min-h-[2rem]">
        <span className="text-[10px] uppercase tracking-wide text-system-gray-500">
          Section
        </span>
        {(["top", "center", "bottom"] as const).map(anchor => (
          <button
            key={anchor}
            type="button"
            onClick={() => addSection(anchor)}
            className="btn btn-xs bg-system-gray-800 border-system-gray-700 text-system-gray-300 hover:text-white capitalize"
            title={`Add a ${anchor}-anchored section`}
          >
            + {anchor}
          </button>
        ))}
        <span className="text-[10px] uppercase tracking-wide text-system-gray-500 ml-2">
          Item
        </span>
        <button
          type="button"
          onClick={() => addItem(makeTextItem(doc, brandKit, "tag"))}
          className="btn btn-xs bg-system-gray-800 border-system-gray-700 text-system-gray-300 hover:text-white"
        >
          Tag
        </button>
        <button
          type="button"
          onClick={() => addItem(makeTextItem(doc, brandKit, "headline"))}
          className="btn btn-xs bg-system-gray-800 border-system-gray-700 text-system-gray-300 hover:text-white"
        >
          Headline
        </button>
        <button
          type="button"
          onClick={() => addItem(makeTextItem(doc, brandKit, "subline"))}
          className="btn btn-xs bg-system-gray-800 border-system-gray-700 text-system-gray-300 hover:text-white"
        >
          Text
        </button>
        <button
          type="button"
          onClick={() => addItem(makeTextItem(doc, brandKit, "button"))}
          className="btn btn-xs bg-system-gray-800 border-system-gray-700 text-system-gray-300 hover:text-white"
        >
          Button
        </button>
        <button
          type="button"
          onClick={() => addItem(makeDividerItem(doc, brandKit))}
          className="btn btn-xs bg-system-gray-800 border-system-gray-700 text-system-gray-300 hover:text-white"
        >
          Bar
        </button>
        <button
          type="button"
          onClick={() => addItem(makeLogoItem(doc))}
          disabled={!logoUrl}
          title={
            logoUrl ? undefined : "Upload a logo in the Brand Kit to enable"
          }
          className="btn btn-xs bg-system-gray-800 border-system-gray-700 text-system-gray-300 hover:text-white disabled:opacity-40"
        >
          Logo
        </button>
        <button
          type="button"
          onClick={() => addItem(makeSpacerItem(doc))}
          className="btn btn-xs bg-system-gray-800 border-system-gray-700 text-system-gray-300 hover:text-white"
        >
          Spacer
        </button>
      </div>

      {/* Inspector */}
      {selection && (
        <Inspector
          selection={selection}
          brandKit={brandKit}
          onPatchSection={patchSection}
          onPatchItem={patchItem}
          onMoveItem={moveItem}
          onRemove={removeSelected}
        />
      )}

      {/* Canvas */}
      <div
        ref={containerRef}
        className="relative w-full overflow-hidden rounded-lg bg-black select-none"
        style={{ aspectRatio: `${doc.canvas.width} / ${doc.canvas.height}` }}
        onPointerDown={() => onSelectId(null)}
      >
        {/* cover (not fill): when the canvas ratio differs from the media,
            the export center-crops the same way, keeping WYSIWYG. */}
        {media === null ? (
          <div className="absolute inset-0" style={CHECKERBOARD} />
        ) : media.type === "video" ? (
          <video
            src={media.src}
            className="absolute inset-0 w-full h-full object-cover"
            muted
            loop
            autoPlay
            playsInline
          />
        ) : (
          <img
            src={media.src}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
          />
        )}

        {scale > 0 &&
          layout.bands.map(band => {
            const { background } = band.section
            if (!background) return null
            return (
              <div
                key={`band-${band.section.id}`}
                style={{
                  position: "absolute",
                  left: band.x * scale,
                  ...verticalStyle(band.y, band.height, band.section.anchor),
                  width: band.width * scale,
                  height: band.height * scale,
                  borderRadius: (background.radius ?? 0) * scale,
                  background: background.gradient
                    ? `linear-gradient(${GRADIENT_CSS_ANGLE[background.gradient.direction]}, ${background.gradient.from}, ${background.gradient.to})`
                    : hexWithAlpha(background.color, background.opacity ?? 1),
                  cursor: "pointer",
                  ...selectionStyle(band.section.id),
                }}
                {...hoverHandlers(band.section.id)}
                onPointerDown={e => {
                  e.stopPropagation()
                  onSelectId(band.section.id)
                }}
              />
            )
          })}

        {scale > 0 &&
          layout.boxes.map(box => {
            const baseStyle: CSSProperties = {
              position: "absolute",
              left: box.x * scale,
              ...verticalStyle(
                box.y,
                box.height,
                anchorForSection(box.sectionId),
                !!box.item.position,
              ),
              width: box.width * scale,
              height: box.height * scale,
              cursor: "move",
              touchAction: "none",
              ...selectionStyle(box.item.id),
              ...dragStyle(box.item.id),
            }
            const dragHandlers = {
              onPointerDown: (e: ReactPointerEvent<HTMLDivElement>) =>
                handleItemPointerDown(e, box.item.id, box.x, box.y),
              onPointerMove: handleItemPointerMove,
              onPointerUp: handleItemPointerUp,
              ...hoverHandlers(box.item.id),
              title:
                "Drag to place freely (leaves the flow — use Dock to return)",
            }
            if (box.kind === "spacer") {
              return (
                <div
                  key={box.item.id}
                  style={{
                    ...baseStyle,
                    // Give zero-width/height spacers a clickable footprint.
                    minWidth: 12,
                    minHeight: 12,
                    outline:
                      box.item.id === selectedId
                        ? `1px dashed ${BRAND}`
                        : box.item.id === hoveredId
                          ? `1px dashed ${BRAND}66`
                          : undefined,
                  }}
                  {...dragHandlers}
                />
              )
            }
            if (box.kind === "divider" && box.item.type === "divider") {
              return (
                <div
                  key={box.item.id}
                  style={{
                    ...baseStyle,
                    background: box.item.color,
                    borderRadius: (box.item.radius ?? 0) * scale,
                  }}
                  {...dragHandlers}
                />
              )
            }
            if (box.kind === "logo") {
              return (
                <div
                  key={box.item.id}
                  className={cn(
                    "flex items-center justify-center",
                    !logoUrl && "bg-system-gray-800/60",
                  )}
                  style={baseStyle}
                  {...dragHandlers}
                >
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt="Logo"
                      className="max-w-full max-h-full object-contain pointer-events-none"
                      draggable={false}
                    />
                  ) : (
                    <span className="text-[10px] text-system-gray-400">
                      Logo
                    </span>
                  )}
                </div>
              )
            }
            if (box.kind !== "text") return null
            const item = box.item
            const align = item.textAlign ?? "left"
            return (
              <div
                key={item.id}
                style={{
                  ...baseStyle,
                  ...(item.background
                    ? {
                        backgroundColor: hexWithAlpha(
                          item.background.color,
                          item.background.opacity ?? 1,
                        ),
                        borderRadius: item.background.radius * scale,
                        ...(item.background.borderColor &&
                        item.background.borderWidth
                          ? {
                              border: `${item.background.borderWidth * scale}px solid ${item.background.borderColor}`,
                            }
                          : {}),
                      }
                    : {}),
                }}
                {...dragHandlers}
              >
                <div
                  style={{
                    position: "absolute",
                    left: (box.textX - box.x) * scale,
                    top: (box.textY - box.y) * scale,
                    width: box.textWidth * scale,
                    fontFamily: fontStack(item.fontFamily),
                    fontWeight: item.fontWeight,
                    fontSize: item.fontSize * scale,
                    color: item.color,
                    textAlign: align,
                    lineHeight: `${box.lineHeightPx * scale}px`,
                    letterSpacing: item.letterSpacing
                      ? `${item.letterSpacing * scale}px`
                      : undefined,
                    textDecoration: item.underline ? "underline" : undefined,
                    textShadow: item.shadow
                      ? `0 ${item.fontSize * scale * 0.05}px ${item.fontSize * scale * 0.18}px rgba(0,0,0,0.55)`
                      : undefined,
                    whiteSpace: "pre",
                    pointerEvents: "none",
                  }}
                >
                  {/* Render the SOLVED lines (not free-wrapping text) so the
                      editor shows exactly what the exporter draws. */}
                  {box.lines.map((line, i) => (
                    <div key={`${i}-${line}`}>{line}</div>
                  ))}
                </div>
              </div>
            )
          })}
      </div>
    </div>
  )
}

// ── Inspector ────────────────────────────────────────────────────────────

// Slimmed control classes: DaisyUI's default select/input heights + arrow
// padding made the inspector row tall enough to push the canvas out of view.
// h-6/min-h-0 + tight padding keep the whole inspector one thin row.
const inputClass =
  "input input-xs h-6 min-h-0 px-1.5 bg-system-gray-800 border-system-gray-700 text-white"
const selectClass =
  "select select-xs h-6 min-h-0 pl-1.5 pr-6 bg-system-gray-800 border-system-gray-700 text-white"

function ColorSwatch({
  value,
  onChange,
  title,
}: {
  value: string
  onChange: (color: string) => void
  title: string
}) {
  return (
    <input
      type="color"
      value={value.slice(0, 7)}
      onChange={e => onChange(e.target.value)}
      title={title}
      className="w-6 h-6 rounded border border-system-gray-700 bg-transparent cursor-pointer p-0"
    />
  )
}

interface InspectorProps {
  selection: NonNullable<Selection>
  brandKit?: BrandKit
  onPatchSection: (id: string, patch: Partial<OverlaySection>) => void
  onPatchItem: (id: string, patch: Partial<OverlayItem>) => void
  onMoveItem: (direction: -1 | 1) => void
  onRemove: () => void
}

function Inspector({
  selection,
  brandKit,
  onPatchSection,
  onPatchItem,
  onMoveItem,
  onRemove,
}: InspectorProps) {
  const fontOptions = [
    ...new Set(
      [
        brandKit?.fonts?.heading,
        brandKit?.fonts?.body,
        ...(brandKit?.customFonts?.map(font => font.family) ?? []),
        "Inter",
      ].filter((f): f is string => !!f),
    ),
  ]

  return (
    /* One non-wrapping row that scrolls horizontally — wrapping made the
       inspector grow vertically and shove the canvas below the fold. */
    <div className="flex items-center gap-1.5 flex-nowrap overflow-x-auto [&>*]:shrink-0 bg-system-gray-900/80 border border-system-gray-800 rounded-lg px-2 py-1">
      {selection.kind === "section" ? (
        <SectionControls
          section={selection.section}
          brandKit={brandKit}
          onPatch={patch => onPatchSection(selection.section.id, patch)}
        />
      ) : (
        <>
          <ItemControls
            item={selection.item}
            fontOptions={fontOptions}
            brandKit={brandKit}
            onPatch={patch => onPatchItem(selection.item.id, patch)}
          />
          {selection.item.position ? (
            <button
              type="button"
              onClick={() =>
                onPatchItem(selection.item.id, { position: undefined })
              }
              className="btn btn-xs bg-system-gray-800 border-system-gray-700 text-system-gray-300 hover:text-white"
              title="Return this free-placed item to its section's flow"
            >
              Dock
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => onMoveItem(-1)}
                className="btn btn-xs btn-ghost text-system-gray-400"
                title="Move earlier in the stack"
              >
                <ArrowUpIcon className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => onMoveItem(1)}
                className="btn btn-xs btn-ghost text-system-gray-400"
                title="Move later in the stack"
              >
                <ArrowDownIcon className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="btn btn-xs btn-ghost text-system-gray-400 hover:text-system-red-400"
        aria-label={
          selection.kind === "section" ? "Delete section" : "Delete item"
        }
        title={selection.kind === "section" ? "Delete section" : "Delete item"}
      >
        <TrashIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

function SectionControls({
  section,
  brandKit,
  onPatch,
}: {
  section: OverlaySection
  brandKit?: BrandKit
  onPatch: (patch: Partial<OverlaySection>) => void
}) {
  // Narrowing on `section.background` doesn't survive into JSX callbacks.
  const patchBandColor = (color: string): void => {
    if (!section.background) return
    onPatch({ background: { ...section.background, color } })
  }

  return (
    <>
      <span className="text-[10px] uppercase tracking-wide text-system-gray-500">
        Section
      </span>
      <select
        value={section.anchor}
        onChange={e =>
          onPatch({ anchor: e.target.value as OverlaySection["anchor"] })
        }
        className={selectClass}
        title="Vertical anchor"
      >
        <option value="top">Top</option>
        <option value="center">Center</option>
        <option value="bottom">Bottom</option>
      </select>
      <select
        value={section.direction}
        onChange={e =>
          onPatch({ direction: e.target.value as OverlaySection["direction"] })
        }
        className={selectClass}
        title="Stack direction"
      >
        <option value="column">Column</option>
        <option value="row">Row</option>
      </select>
      <select
        value={section.align}
        onChange={e =>
          onPatch({ align: e.target.value as OverlaySection["align"] })
        }
        className={selectClass}
        title="Alignment"
      >
        <option value="start">Left</option>
        <option value="center">Center</option>
        <option value="end">Right</option>
      </select>
      {section.direction === "row" && (
        <select
          value={section.justify}
          onChange={e =>
            onPatch({ justify: e.target.value as OverlaySection["justify"] })
          }
          className={selectClass}
          title="Row distribution"
        >
          <option value="start">Pack left</option>
          <option value="center">Pack center</option>
          <option value="end">Pack right</option>
          <option value="space-between">Space between</option>
        </select>
      )}
      <input
        type="number"
        value={section.gap}
        onChange={e => onPatch({ gap: Math.max(0, Number(e.target.value)) })}
        className={cn(inputClass, "w-14")}
        title="Gap (px)"
      />
      <input
        type="number"
        value={section.paddingX}
        onChange={e =>
          onPatch({ paddingX: Math.max(0, Number(e.target.value)) })
        }
        className={cn(inputClass, "w-16")}
        title="Side inset (px)"
      />
      <input
        type="number"
        value={section.paddingY}
        onChange={e =>
          onPatch({ paddingY: Math.max(0, Number(e.target.value)) })
        }
        className={cn(inputClass, "w-16")}
        title="Inset from anchored edge (px)"
      />
      {section.background ? (
        <>
          <ColorSwatch
            value={section.background.color}
            onChange={patchBandColor}
            title="Band color"
          />
          <button
            type="button"
            onClick={() =>
              onPatch({
                background: section.background?.gradient
                  ? { color: section.background.color, opacity: 0.6 }
                  : {
                      color: section.background?.color ?? "#000000",
                      gradient: {
                        from: "#00000000",
                        to: "#000000CC",
                        direction: "vertical",
                      },
                    },
              })
            }
            className={cn(
              "btn btn-xs btn-ghost",
              section.background.gradient
                ? "text-white"
                : "text-system-gray-500",
            )}
            title="Toggle gradient band (fade-in scrim)"
          >
            Gradient
          </button>
          <button
            type="button"
            onClick={() => onPatch({ background: undefined })}
            className="btn btn-xs btn-ghost text-system-gray-400"
            title="Remove background band"
          >
            No band
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={() =>
            onPatch({
              background: {
                // Brand kit first — hardcoded black only when the kit has
                // no usable surface color.
                color:
                  brandKit?.colors?.secondary ||
                  brandKit?.colors?.background ||
                  "#000000",
                opacity: 0.6,
              },
            })
          }
          className="btn btn-xs btn-ghost text-system-gray-400"
          title="Add a full-width background band"
        >
          Band
        </button>
      )}
    </>
  )
}

function ItemControls({
  item,
  fontOptions,
  brandKit,
  onPatch,
}: {
  item: OverlayItem
  fontOptions: string[]
  brandKit?: BrandKit
  onPatch: (patch: Partial<OverlayItem>) => void
}) {
  // Narrowing on `item.background` doesn't survive into JSX callbacks.
  const patchTextBackground = (color: string): void => {
    if (item.type !== "text" || !item.background) return
    onPatch({
      background: {
        ...item.background,
        color,
        ...(item.background.borderColor ? { borderColor: color } : {}),
      },
    })
  }

  const setButtonStyle = (style: "filled" | "outline"): void => {
    if (item.type !== "text" || !item.background) return
    onPatch({
      background:
        style === "filled"
          ? {
              ...item.background,
              opacity: 1,
              borderColor: undefined,
              borderWidth: undefined,
            }
          : {
              ...item.background,
              opacity: 0,
              borderColor: item.background.color,
              borderWidth: Math.max(2, Math.round(item.fontSize * 0.08)),
            },
    })
  }

  if (item.type === "text") {
    return (
      <>
        <input
          value={item.text}
          onChange={e => onPatch({ text: e.target.value })}
          placeholder="Text"
          className={cn(inputClass, "w-44")}
        />
        <select
          value={item.fontFamily}
          onChange={e => onPatch({ fontFamily: e.target.value })}
          className={selectClass}
          title="Font"
        >
          {fontOptions.map(font => (
            <option key={font} value={font}>
              {font}
            </option>
          ))}
        </select>
        <input
          type="number"
          value={item.fontSize}
          onChange={e => onPatch({ fontSize: Number(e.target.value) || 8 })}
          className={cn(inputClass, "w-16")}
          title="Font size (px)"
        />
        <ColorSwatch
          value={item.color}
          onChange={color => onPatch({ color })}
          title="Text color"
        />
        <select
          value={item.fontWeight}
          onChange={e => onPatch({ fontWeight: Number(e.target.value) })}
          className={selectClass}
          title="Weight"
        >
          {[400, 500, 600, 700, 800, 900].map(weight => (
            <option key={weight} value={weight}>
              {weight}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => onPatch({ uppercase: !item.uppercase })}
          className={cn(
            "btn btn-xs btn-ghost",
            item.uppercase ? "text-white" : "text-system-gray-500",
          )}
          title="Uppercase"
        >
          AA
        </button>
        <button
          type="button"
          onClick={() => onPatch({ underline: !item.underline })}
          className={cn(
            "btn btn-xs btn-ghost underline",
            item.underline ? "text-white" : "text-system-gray-500",
          )}
          title="Underline"
        >
          U
        </button>
        <button
          type="button"
          onClick={() => onPatch({ shadow: !item.shadow })}
          className={cn(
            "btn btn-xs btn-ghost",
            item.shadow ? "text-white" : "text-system-gray-500",
          )}
          title="Drop shadow (legibility on busy media)"
        >
          Shadow
        </button>
        {item.background ? (
          <>
            <ColorSwatch
              value={item.background.color}
              onChange={patchTextBackground}
              title="Button color"
            />
            <select
              value={item.background.borderColor ? "outline" : "filled"}
              onChange={e =>
                setButtonStyle(e.target.value as "filled" | "outline")
              }
              className={selectClass}
              title="Button style"
            >
              <option value="filled">Filled</option>
              <option value="outline">Outline</option>
            </select>
            <button
              type="button"
              onClick={() => onPatch({ background: undefined })}
              className="btn btn-xs btn-ghost text-system-gray-400"
              title="Remove button background"
            >
              None
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() =>
              onPatch({
                background: {
                  color: brandKit?.colors?.primary || "#111111",
                  paddingX: 24,
                  paddingY: 12,
                  radius: 999,
                },
              })
            }
            className="btn btn-xs btn-ghost text-system-gray-400"
            title="Add a button-style background"
          >
            Button
          </button>
        )}
      </>
    )
  }

  if (item.type === "divider") {
    return (
      <>
        <ColorSwatch
          value={item.color}
          onChange={color => onPatch({ color })}
          title="Bar color"
        />
        <input
          type="number"
          value={item.width}
          onChange={e => onPatch({ width: Number(e.target.value) || 2 })}
          className={cn(inputClass, "w-20")}
          title="Width (px)"
        />
        <input
          type="number"
          value={item.height}
          onChange={e => onPatch({ height: Number(e.target.value) || 1 })}
          className={cn(inputClass, "w-16")}
          title="Height (px)"
        />
      </>
    )
  }

  if (item.type === "logo") {
    return (
      <>
        <input
          type="number"
          value={item.width}
          onChange={e => onPatch({ width: Number(e.target.value) || 8 })}
          className={cn(inputClass, "w-20")}
          title="Max width (px)"
        />
        <input
          type="number"
          value={item.height}
          onChange={e => onPatch({ height: Number(e.target.value) || 8 })}
          className={cn(inputClass, "w-20")}
          title="Max height (px)"
        />
      </>
    )
  }

  return (
    <input
      type="number"
      value={item.size}
      onChange={e => onPatch({ size: Number(e.target.value) || 1 })}
      className={cn(inputClass, "w-20")}
      title="Spacer size (px)"
    />
  )
}
